import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { documentApi, type DocumentDetailResponse } from '../services/partyApi';
import { useGoBack } from '../components/useGoBack';

export function DocumentDetail() {
  const { doc_number } = useParams<{ doc_number: string }>();
  const [data, setData] = useState<DocumentDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const goBack = useGoBack();

  useEffect(() => {
    if (!doc_number) return;
    documentApi.get(doc_number).then(setData).catch(e => setErr(e?.message ?? 'Failed to load'));
  }, [doc_number]);

  if (err) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto">
        <button onClick={goBack} className="text-[12px] text-text-faint font-mono hover:text-text-dim">← back</button>
        <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading document…</div>;
  }

  const d = data.document;
  const trsLabel = data.trs.map(t => t.trs_key).join(' · ');

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto pb-20">
      <button onClick={goBack} className="text-[12px] text-text-faint font-mono hover:text-text-dim">← back</button>

      <header className="mt-2 flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">recorder document</div>
          <div className="font-display text-[26px] leading-tight">
            <span className="text-text-dim">#</span><em className="text-accent not-italic">{d.doc_number}</em>
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono space-x-2">
            <span>{d.doc_type ?? 'unknown type'}</span>
            <span className="text-text-ghost">·</span>
            <span>{d.recording_date ? `recorded ${d.recording_date}` : 'unrecorded'}</span>
            <span className="text-text-ghost">·</span>
            <span>{trsLabel || 'no TRS'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={'px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded border ' +
            (d.enrichment_status === 'enriched_from_pipeline'
              ? 'border-green/40 bg-green/10 text-green'
              : d.enrichment_status === 'pipeline_only'
                ? 'border-amber/40 bg-amber/10 text-amber'
                : 'border-border bg-bg-input/40 text-text-dim')}>
            {d.enrichment_status ?? '—'}
          </span>
        </div>
      </header>

      {d.review_conflict && (
        <div className="mt-4 text-[11px] text-amber bg-amber/10 border border-amber/30 rounded px-4 py-2 font-mono">
          ⚠ pipeline and recorder disagree on {Array.isArray(d.review_conflict_fields) ? d.review_conflict_fields.length : 0} field(s) — needs manual review
        </div>
      )}

      <section className="mt-6 grid grid-cols-4 gap-3">
        <StatCell label="effective" value={d.effective_date ?? '—'} />
        <StatCell label="primary term" value={d.primary_term_months != null ? `${d.primary_term_months} mo` : '—'} />
        <StatCell label="royalty" value={d.royalty_pct != null ? `${(d.royalty_pct * 100).toFixed(3)}%` : '—'} />
        <StatCell label="gross acreage" value={d.gross_acreage != null ? d.gross_acreage.toFixed(2) : '—'} />
      </section>

      <section className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">
            Grantors ({data.grantors.length})
          </div>
          {data.grantors.length === 0 ? (
            <div className="text-[12px] text-text-faint italic">no linked grantors</div>
          ) : (
            <div className="bg-bg-card-soft border border-border rounded">
              {data.grantors.map(g => (
                <Link key={`${g.party_id}-${g.ordinal}`} to={`/mi/party/${g.party_id}`}
                  className="block px-4 py-2 border-b border-border last:border-b-0 hover:bg-bg-hover/40">
                  <div className="text-[12px] text-text">{g.display_name_natural ?? g.display_name}</div>
                  <div className="text-[10px] text-text-faint font-mono mt-0.5">{g.entity_type}</div>
                </Link>
              ))}
            </div>
          )}
          {d.raw_grantor_text && (
            <details className="mt-2">
              <summary className="text-[10px] text-text-faint font-mono cursor-pointer hover:text-text-dim">raw grantor text</summary>
              <div className="mt-1 text-[11px] text-text-dim font-mono whitespace-pre-wrap">{d.raw_grantor_text}</div>
            </details>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">
            Grantees ({data.grantees.length})
          </div>
          {data.grantees.length === 0 ? (
            <div className="text-[12px] text-text-faint italic">no linked grantees</div>
          ) : (
            <div className="bg-bg-card-soft border border-border rounded">
              {data.grantees.map(g => (
                <Link key={`${g.party_id}-${g.ordinal}`} to={`/mi/party/${g.party_id}`}
                  className="block px-4 py-2 border-b border-border last:border-b-0 hover:bg-bg-hover/40">
                  <div className="text-[12px] text-text">{g.display_name_natural ?? g.display_name}</div>
                  <div className="text-[10px] text-text-faint font-mono mt-0.5">{g.entity_type}</div>
                </Link>
              ))}
            </div>
          )}
          {d.raw_grantee_text && (
            <details className="mt-2">
              <summary className="text-[10px] text-text-faint font-mono cursor-pointer hover:text-text-dim">raw grantee text</summary>
              <div className="mt-1 text-[11px] text-text-dim font-mono whitespace-pre-wrap">{d.raw_grantee_text}</div>
            </details>
          )}
        </div>
      </section>

      <section className="mt-6">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">TRS coverage</div>
        {data.trs.length === 0 ? (
          <div className="text-[12px] text-text-faint italic">no TRS rows</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.trs.map((t, i) => (
              <span key={i} className="px-2 py-1 text-[11px] font-mono bg-bg-input/60 border border-border rounded text-text-dim">
                {t.trs_key}
              </span>
            ))}
          </div>
        )}
      </section>

      {d.raw_legal_description && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Legal description (raw)</div>
          <div className="bg-bg-card-soft border border-border rounded px-4 py-3 text-[11px] text-text-dim font-mono whitespace-pre-wrap">
            {d.raw_legal_description}
          </div>
        </section>
      )}

      {data.referenced_by_pool_orders.length > 0 && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Referenced by pool orders</div>
          <div className="bg-bg-card-soft border border-border rounded">
            {data.referenced_by_pool_orders.map(po => (
              <Link key={po.id} to={`/mi/pool-orders/${po.pool_order_id}`}
                className="block grid grid-cols-[120px_1fr_1fr_100px] gap-3 px-4 py-2 border-b border-border last:border-b-0 hover:bg-bg-hover/40"
                style={{ display: 'grid' }}>
                <span className="text-accent font-mono text-[11px]">{po.docket_number ?? po.pool_order_id.slice(0,8)}</span>
                <span className="text-text-dim text-[11px]">lessor: {po.lessor_name}</span>
                <span className="text-text-dim text-[11px]">lessee: {po.lessee_name ?? '—'}</span>
                <span className="text-text-faint font-mono text-[11px] text-right">{po.lease_date ?? '—'}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {data.batch_events.length > 0 && (
        <section className="mt-6">
          <details>
            <summary className="text-[10px] uppercase tracking-wider font-mono text-text-faint cursor-pointer hover:text-text-dim">
              Ingest history ({data.batch_events.length})
            </summary>
            <div className="mt-2 bg-bg-card-soft border border-border rounded">
              {data.batch_events.map(e => (
                <div key={e.id} className="grid grid-cols-[150px_100px_1fr] gap-3 px-4 py-2 border-b border-border last:border-b-0 text-[11px] font-mono">
                  <span className="text-text-dim">{e.created_at.slice(0,16).replace('T',' ')}</span>
                  <span className="text-text">{e.event_type}</span>
                  <span className="text-text-faint truncate">{e.changed_fields ? JSON.stringify(e.changed_fields).slice(0, 80) : ''}</span>
                </div>
              ))}
            </div>
          </details>
        </section>
      )}
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-bg-card border border-border rounded px-4 py-3">
      <div className="font-display text-[18px] leading-tight text-text">{value}</div>
      <div className="text-[10px] text-text-faint font-mono uppercase tracking-wider mt-1.5">{label}</div>
    </div>
  );
}
